import i18next from 'i18next';

const MESSAGE_CODE_REGEX = /"Message"\s*:\s*"([^"]+)"/i;
const UPPERCASE_CODE_REGEX = /\b([A-Z][A-Z0-9_]{2,})\b/;

const SSO_ERROR_TRANSLATION_KEYS: Record<string, string> = {
  SERVICE_UNAVAILABLE: 'loginSso.errors.SERVICE_UNAVAILABLE',
  AUTH_OTP_EXPIRED: 'loginSso.errors.AUTH_OTP_EXPIRED',
  AUTH_OTP_MAX_ATTEMPTS_EXCEEDED: 'loginSso.errors.AUTH_OTP_MAX_ATTEMPTS_EXCEEDED',
  AUTH_INVALID_OTP: 'loginSso.errors.AUTH_INVALID_OTP',
  AUTH_SESSION_INVALID: 'loginSso.errors.AUTH_SESSION_INVALID',
  AUTH_USER_NOT_VERIFIED: 'loginSso.errors.AUTH_USER_NOT_VERIFIED',
  AUTH_USER_NOT_ACTIVE: 'loginSso.errors.AUTH_USER_NOT_ACTIVE',
  AUTH_INVALID_PORTAL: 'loginSso.errors.AUTH_INVALID_PORTAL',
  AUTH_OTP_EMAIL_ONLY: 'loginSso.errors.AUTH_OTP_EMAIL_ONLY',
  OTP_LOGIN_RATE_LIMIT: 'loginSso.errors.OTP_LOGIN_RATE_LIMIT',
  INVALID_OTP_EXPIRED: 'loginSso.errors.INVALID_OTP_EXPIRED',
  INVALID_OTP_ATTEMPTS: 'loginSso.errors.INVALID_OTP_ATTEMPTS',
  INVALID_OTP: 'loginSso.errors.INVALID_OTP',
  OTP_SESSION_INVALID: 'loginSso.errors.OTP_SESSION_INVALID',
  SSO_ERROR: 'loginSso.errors.SSO_ERROR',
  SSO_PORTAL_ROLES_UPDATE_FAILED: 'loginSso.errors.SSO_PORTAL_ROLES_UPDATE_FAILED',
  SSO_PORTAL_ACCESS_UPDATE_FAILED: 'loginSso.errors.SSO_PORTAL_ACCESS_UPDATE_FAILED',
  USER_NOT_FOUND: 'loginSso.errors.USER_NOT_FOUND',
  USER_NOT_PROVISIONED: 'loginSso.errors.USER_NOT_PROVISIONED',
  USER_NO_PORTAL_ACCESS: 'loginSso.errors.USER_NO_PORTAL_ACCESS',
  USER_INACTIVE: 'loginSso.errors.USER_INACTIVE',
  ACCOUNT_NOT_AVAILABLE: 'loginSso.errors.ACCOUNT_NOT_AVAILABLE',
  BOOTSTRAP_NOT_CONFIGURED: 'loginSso.errors.BOOTSTRAP_NOT_CONFIGURED',
  BOOTSTRAP_TOKEN_INVALID: 'loginSso.errors.BOOTSTRAP_TOKEN_INVALID',
  USER_NO_ADMIN_ACCESS: 'loginSso.errors.USER_NO_ADMIN_ACCESS',
  UNEXPECTED_ERROR: 'loginSso.errors.UNEXPECTED_ERROR',
  MISSING_ACCESS_TOKEN: 'loginSso.errors.MISSING_ACCESS_TOKEN',
  REQUEST_OTP_FALLBACK: 'loginSso.errors.REQUEST_OTP_FALLBACK',
  VERIFY_OTP_FALLBACK: 'loginSso.errors.VERIFY_OTP_FALLBACK',
};

export const extractSsoMessageCode = ( error: unknown ) => {
  if ( !error ) return undefined;

  if ( typeof error === 'object' ) {
    const typedError = error as { Message?: string; message?: string; detail?: string };
    if ( typedError.Message ) return typedError.Message.toUpperCase();
    if ( typedError.message ) return typedError.message.toUpperCase();
    if ( typedError.detail ) {
      const detailMatch = typedError.detail.match( UPPERCASE_CODE_REGEX );
      if ( detailMatch?.[1] ) return detailMatch[1].toUpperCase();
    }
  }

  const asString = String( error );
  if ( !asString ) return undefined;

  const jsonMessageMatch = asString.match( MESSAGE_CODE_REGEX );
  if ( jsonMessageMatch?.[1] ) return jsonMessageMatch[1].toUpperCase();

  const directCodeMatch = asString.match( UPPERCASE_CODE_REGEX );
  if ( directCodeMatch?.[1] ) return directCodeMatch[1].toUpperCase();

  return undefined;
};

export const isAuthSsoErrorCode = ( code?: string ) => Boolean( code?.startsWith( 'AUTH_' ) );
const AUTH_SSO_NON_RESETTABLE_CODES = new Set( [
  'AUTH_INVALID_OTP',
] );

export const shouldResetSsoFlowByCode = ( code?: string ) => (
  Boolean( code?.startsWith( 'AUTH_' ) ) && !AUTH_SSO_NON_RESETTABLE_CODES.has( String( code ) )
);

export const translateSsoErrorCode = ( code?: string, fallbackCode?: string ) => {
  const finalCode = code || fallbackCode;
  if ( !finalCode ) return '';

  const translationKey = SSO_ERROR_TRANSLATION_KEYS[finalCode];
  if ( !translationKey ) return finalCode;

  const translated = i18next.t( translationKey );
  return translated === translationKey ? finalCode : translated;
};
